import '../../i18n';
import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

export default function ExpandablePreviewText({ children, lines, lineHeight, contentKey, resetKey, expanded: controlledExpanded, onExpandedChange }) {
    const { t } = useTranslation();
    const contentRef = React.useRef(null);
    const [localExpanded, setLocalExpanded] = React.useState(false);
    const expanded = controlledExpanded ?? localExpanded;
    const [overflows, setOverflows] = React.useState(false);
    const contentId = React.useId();
    const collapsedHeight = lines * lineHeight;

    React.useEffect(() => setLocalExpanded(false), [resetKey]);
    React.useEffect(() => {
        const content = contentRef.current;
        if (!content) return;
        const measure = () => {
            setOverflows(content.scrollHeight > collapsedHeight + 1);
        };
        measure();
        const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
        observer?.observe(content);
        window.addEventListener('resize', measure);
        return () => {
            observer?.disconnect();
            window.removeEventListener('resize', measure);
        };
    }, [contentKey, collapsedHeight]);

    return <Container data-preview-text>
        <Viewport id={contentId} $height={collapsedHeight} $expanded={expanded} $lineHeight={lineHeight}>
            <div ref={contentRef}>{children}</div>
        </Viewport>
        {overflows ? <Toggle
            type="button"
            aria-expanded={expanded}
            aria-controls={contentId}
            onMouseDown={event => { event.preventDefault(); event.stopPropagation(); }}
            onKeyDown={event => event.stopPropagation()}
            onClick={event => {
                event.preventDefault();
                event.stopPropagation();
                if (controlledExpanded !== undefined) onExpandedChange?.(!expanded);
                else setLocalExpanded(value => !value);
            }}
        >{t(expanded ? 'messages:showLess' : 'messages:showMore')}</Toggle> : null}
    </Container>;
}

const Container = styled.div`
    flex: 1;
    min-width: 0;
    width: 100%;
`;
const Viewport = styled.div`
    max-height: ${({ $expanded, $height }) => $expanded ? 'none' : `${$height}px`};
    overflow: ${({ $expanded }) => $expanded ? 'visible' : 'hidden'};
    line-height: ${({ $lineHeight }) => `${$lineHeight}px`};
    > div { display: flow-root; }
`;
const Toggle = styled.button`
    display: block;
    margin: 6px 0 0;
    padding: 0;
    border: 0;
    background: none;
    color: rgba(255, 255, 255, 0.65);
    font: inherit;
    font-size: 12px;
    font-weight: 500;
    line-height: 18px;
    cursor: pointer;
    &:hover { color: white; }
    &:focus-visible { outline: 1px solid currentColor; outline-offset: 3px; }
`;
